import { io, type Socket } from 'socket.io-client'
import type {
  NativeMeetingSession,
  NativeTranscriptSegmentPage,
  NativeTranscriptionProvider,
} from '@ai-meeting/shared'
import { API_BASE, apiClient, toApiError } from './api'
import { getAccessToken } from './session'

export interface CreateNativeSessionPayload {
  meetingId: string
  provider: NativeTranscriptionProvider
  meetingReference: string
}

export interface GoogleMeetConnectionStatus {
  provider: 'google'
  status: 'ACTIVE' | 'REAUTH_REQUIRED' | 'REVOKED' | 'CONNECTION_REQUIRED'
  scopes: string[]
  expiresAt: string | null
}

export interface NativeAudioAccess {
  url: string
  expiresAt: string
}

export async function fetchGoogleMeetConnection(): Promise<GoogleMeetConnectionStatus> {
  const { data } = await apiClient.get<GoogleMeetConnectionStatus>(
    '/api/native-transcription/google/connection'
  )
  return data
}

export async function getGoogleMeetConnectUrl(): Promise<string> {
  const { data } = await apiClient.get<{ authorizationUrl: string }>(
    '/api/native-transcription/google/connect'
  )
  return data.authorizationUrl
}

export async function createNativeSession(
  payload: CreateNativeSessionPayload
): Promise<NativeMeetingSession> {
  try {
    const { data } = await apiClient.post<NativeMeetingSession>(
      '/api/native-transcription/sessions',
      payload
    )
    return data
  } catch (error) {
    throw toApiError(error, 'Failed to create native transcription session')
  }
}

export async function fetchNativeSession(sessionId: string): Promise<NativeMeetingSession> {
  try {
    const { data } = await apiClient.get<NativeMeetingSession>(
      `/api/native-transcription/sessions/${sessionId}`
    )
    return data
  } catch (error) {
    throw toApiError(error, 'Failed to load native transcription session')
  }
}

export async function stopNativeSession(sessionId: string): Promise<NativeMeetingSession> {
  try {
    const { data } = await apiClient.post<NativeMeetingSession>(
      `/api/native-transcription/sessions/${sessionId}/stop`
    )
    return data
  } catch (error) {
    throw toApiError(error, 'Failed to stop native transcription session')
  }
}

export async function fetchNativeAudioAccess(sessionId: string): Promise<NativeAudioAccess> {
  try {
    const { data } = await apiClient.post<NativeAudioAccess>(
      `/api/native-transcription/sessions/${sessionId}/audio-access`
    )
    return data
  } catch (error) {
    throw toApiError(error, 'Failed to authorize Teams gateway audio')
  }
}

export async function fetchNativeSegments(
  sessionId: string,
  afterSequence = 0,
  limit = 100
): Promise<NativeTranscriptSegmentPage> {
  try {
    const { data } = await apiClient.get<NativeTranscriptSegmentPage>(
      `/api/native-transcription/sessions/${sessionId}/segments`,
      { params: { afterSequence, limit } }
    )
    return data
  } catch (error) {
    throw toApiError(error, 'Failed to backfill native transcript')
  }
}

export function createNativeTranscriptSocket(): Socket {
  return io(`${API_BASE}/native-transcription`, {
    autoConnect: false,
    transports: ['websocket'],
    auth: { token: getAccessToken() },
  })
}
